import { useCallback, useEffect, useState } from 'react'
import { Spinner, Text } from '@chakra-ui/react'
import { TicketAssetsEditor } from '@/components/forms/shared/TicketAssetsEditor'
import { TicketService } from '@/lib/services/ticket'
import { ticketToFormValues } from '@/lib/utils/parentTicketFormPayload'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useParentTicketStore } from '@/store/parentTicket'
import type { ParentTicketFormValues } from '@/types/ticket'

type TicketAsset = NonNullable<ParentTicketFormValues['assets']>[number]

type Props = {
  ticketId: string
  clientCode?: string
  storeId?: number | null
  storeName?: string | null
  initialAssets?: TicketAsset[]
  isReadOnly?: boolean
  onSaved?: () => void | Promise<void>
}

export default function AssetsPanel({
  ticketId,
  clientCode,
  storeId,
  storeName,
  initialAssets = [],
  isReadOnly = false,
  onSaved,
}: Props) {
  const { showError, showSuccess } = useToastNotification()
  const getTicketById = useParentTicketStore((s) => s.getTicketById)
  const [assets, setAssets] = useState<TicketAsset[]>(initialAssets)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setAssets(initialAssets)
  }, [initialAssets])

  const persistAssets = useCallback(
    async (nextAssets: TicketAsset[]) => {
      const ticket = getTicketById(ticketId) as ReturnType<typeof getTicketById> & Record<string, unknown>
      const base = ticket ? ticketToFormValues(ticket, ticketId) : null
      if (!base) {
        showError('Could not load ticket data for saving assets')
        return
      }
      setSaving(true)
      try {
        const res = await TicketService.updateParentTicket(ticketId, { ...base, assets: nextAssets })
        if (!res.success) throw new Error(res.message || 'Failed to save assets')
        setAssets(nextAssets)
        showSuccess('Assets updated')
        await onSaved?.()
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to save assets')
      } finally {
        setSaving(false)
      }
    },
    [getTicketById, ticketId, showError, showSuccess, onSaved],
  )

  const handleChange = (next: TicketAsset[]) => {
    if (isReadOnly) return
    void persistAssets(next)
  }

  return (
    <>
      {saving ? (
        <Text fontSize="xs" color="gray.500" mb={2}>
          <Spinner size="xs" mr={2} />
          Saving assets…
        </Text>
      ) : null}
      <TicketAssetsEditor
        assets={assets}
        onChange={handleChange}
        clientCode={clientCode}
        storeId={storeId}
        storeName={storeName}
        isReadOnly={isReadOnly}
      />
    </>
  )
}
